<template>
  <div>
    <div v-if="loading" class="loader loader-positioning" />

    <div id="userRmaListing">
      <table>
        <tr>
          <th class="icon">
            <span @click="refreshRmas()"><i class="fa fa-sync-alt"/></span>
          </th>
          <th>{{ $mlGet("rma-header-make") }}</th>
          <th>{{ $mlGet("rma-header-model") }}</th>
          <th>{{ $mlGet("rma-header-serialnumber") }}</th>
          <th>{{ $mlGet("rma-header-status") }}</th>
          <th>{{ $mlGet("rma-header-timecreated") }}</th>
          <th></th>
          <th></th>
        </tr>
        <tr
          class="clickableRma"
          v-for="rma in userRmas"
          :key="rma.id"
          :class="{ clickedItem: rma.active }"
        >
          <td @click="editHandler(rma)" class="td-small" />
          <td @click="editHandler(rma)">{{ rma.model.make.name }}</td>
          <td @click="editHandler(rma)">{{ rma.model.name }}</td>
          <td @click="editHandler(rma)">{{ rma.serialnumber }}</td>
          <td @click="editHandler(rma)">{{ getStatus(rma) }}</td>
          <td @click="editHandler(rma)">{{ cleanDate(rma.tsCreated) }}</td>
          <td class="icon" @click="editHandler(rma)">
            <span>
              <i class="fa fa-pencil-alt" />
            </span>
          </td>
          <td class="icon" @click="deleteHandler(rma)">
            <span><i class="fa fa-trash-alt"/></span>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { ResponseRma } from "@/types";
import { Component, Vue } from "vue-property-decorator";
import * as Sentry from "@sentry/vue";
import Rma from "../store/RmaModel";

import { cleanDate } from "@/utils/utils";
import { AxiosError } from "axios";

@Component({})
export default class UserRmaListing extends Vue {
  public checkRmas: Array<number> = [];
  public loading = false;

  public waitingOnInput = false;

  get userRmas(): Array<ResponseRma> {
    return this.$store.getters.getUserRmas;
  }

  async refreshRmas(): Promise<void> {
    this.loading = true;
    await this.$store
      .dispatch("downloadUserRmas")
      .catch(err => {
        this.$swal(
          this.$mlGet("error-getUserRmas-head"),
          `Server responed with: ${err.message}`,
          "warning"
        );

        Sentry.captureException(err, {
          extra: {
            userId: this.$store.state.user.id
          }
        });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  getStatus(rma: ResponseRma): string {
    let result = "";
    if (rma.rmaConcept) result = this.$mlGet("rma-status-concept");
    if (rma.rmaReadyForProcessing)
      result = this.$mlGet("rma-status-readyForProcessing");
    if (rma.rmaInProcess) result = this.$mlGet("rma-status-process");
    return result;
  }

  deleteHandler(rma: ResponseRma): void {
    this.$swal({
      title: this.$mlGet("userRmaListing-alert-title"),
      text: this.$mlGet("userRmaListing-alert-text"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$mlGet("userRmaListing-alert-confirm"),
      cancelButtonText: this.$mlGet("userRmaListing-alert-cancel"),
      reverseButtons: true
    }).then(async result => {
      if (result.isConfirmed) {
        await this.$axios.delete(`/rma/${rma.id}`);
        const currentRmaId = this.$store.getters.getCurrentRmaId;
        if (currentRmaId == rma.id) {
          this.$store.commit("cleanRma");
          this.$changePage("ModelSelection", {
            saveRma: false,
            skipPageExit: true,
            clearHistory: true
          });
        }

        this.refreshRmas();
      }
    });
  }

  async editHandler(rma: ResponseRma): Promise<void> {
    if (rma.rmaInProcess) {
      this.$swal(
        this.$mlGet("alert-rmaNotConcept-head"),
        this.$mlGet("alert-rmaNotConcept-text"),
        "warning"
      );
      return;
    }

    let goDefault = true;

    try {
      const axiosResponse = await this.$axios.get(`/rma/byId/${rma.id}`);

      if (!axiosResponse.data) throw new Error(axiosResponse.statusText);

      const fullRma = axiosResponse.data;

      const freshRma = new Rma({
        rmaId: fullRma.id ?? undefined,
        model: {
          fmId: fullRma.model.fmId ?? 0,
          name: fullRma.model.name ?? "",
          make: fullRma.model.make ?? "",
          graphic: fullRma.model.graphic
            ? "data:image/jpeg;base64," + fullRma.model.graphic
            : ""
        },
        reasonForReturn: rma.reasonForReturn ? fullRma.reasonForReturn.id : 0,
        serialnumber: fullRma.serialnumber ?? "",
        faultDescription: fullRma.faultDescription ?? "",
        transportHanexShipping: fullRma.transportHanexShipping ?? false,
        dateOfPurchase: fullRma.dateOfPurchase ?? "",
        transportOriginalPackaging: fullRma.transportOriginalPackaging ?? true,
        transportSecurePacking: fullRma.transportSecurePacking ?? true,
        transportUpsPickup: fullRma.transportUpsPickup ?? false,
        clientReference: fullRma.clientReference ?? "",
        externalReference: fullRma.externalReference ?? "",
        packageWeight: fullRma.packageWeight ?? 0,
        packageHeight: fullRma.packageHeight ?? 0,
        packageLength: fullRma.packageLength ?? 0,
        packageWidth: fullRma.packageWidth ?? 0,
        upsPickupPreveredDate: fullRma.upsPickupPreveredDate ?? "",
        priceQuotation: fullRma.priceQuotation ?? 0,
        files: fullRma.files ?? [],
        receipt: fullRma.receipt ?? {
          filename: "",
          mimetype: "",
          originalname: "",
          size: 0
        },
        transportCouponCode: fullRma.transportCouponCode ?? "",
        dealerOptionsCustomerName: fullRma.dealerOptionsCustomerName ?? "",
        dealerOptionsCustomerEmail: fullRma.dealerOptionsCustomerEmail ?? "",
        dealerOptionsCustomerPhone: fullRma.dealerOptionsCustomerPhone ?? "",
        dealerOptionsCustomerAddress:
          fullRma.dealerOptionsCustomerAddress ?? "",
        dealerOptionsCustomerAddress2:
          fullRma.dealerOptionsCustomerAddress2 ?? "",
        dealerOptionsCustomerCity: fullRma.dealerOptionsCustomerCity ?? "",
        dealerOptionsCustomerAttention:
          fullRma.dealerOptionsCustomerAttention ?? "",
        dealerOptionsCustomerCountry:
          fullRma.dealerOptionsCustomerCountry ?? "",
        dealerOptionsCustomerMessage:
          fullRma.dealerOptionsCustomerMessage ?? "",
        dealerOptionsCustomerPostal: fullRma.dealerOptionsCustomerPostal ?? "",
        dealerOptionsContactDirectly:
          fullRma.dealerOptionsContactDirectly ?? -1,
        dealerOptionsPickupDirectly:
          fullRma.dealerOptionsPickupDirectly ?? false,
        dealerOptionsRetourDirectly:
          fullRma.dealerOptionsRetourDirectly ?? false,
        rmaConfirmationState: fullRma.rmaConfirmationState ?? -1
      });
      this.$store.state.rma = freshRma;
      this.$store.state.userRmas.map((e: ResponseRma) =>
        Vue.set(e, "active", false)
      );
      Vue.set(rma, "active", true);

      if (freshRma.upsPickupPreveredDate) {
        const then = new Date(freshRma.upsPickupPreveredDate);
        then.setHours(0, 0, 0, 0);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (then.getTime() <= now.getTime()) {
          this.$swal({
            title: this.$mlGet("userRmaLinsting-oldDate-head"),
            text: this.$mlGet("userRmaLinsting-oldDate-text"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: this.$mlGet("userRmaLinsting-oldDate-gotoText")
          }).then(res => {
            if (res.isConfirmed) {
              goDefault = false;
              this.$changePage("TransportServicepoints", {
                saveRma: false,
                skipPageExit: true,
                clearHistory: true
              });
            }
          });
        }
      }
      if (goDefault && rma.rmaReadyForProcessing)
        this.$changePage("RmaConfirmation", {
          saveRma: false,
          skipPageExit: true,
          clearHistory: true
        });
      else if (goDefault)
        this.$changePage("ModelSelection", {
          saveRma: false,
          skipPageExit: true,
          clearHistory: true
        });
    } catch (error) {
      const axiosError = error as AxiosError;
      this.$swal(axiosError.message);
    }
  }

  cleanDate = cleanDate;
}
</script>

<style scoped>
#userRmaListing {
  max-height: 20ex;
  overflow: scroll;
  overflow-x: hidden;
}

table {
  border-collapse: collapse;
  width: 100%;
}

th,
td {
  padding: 5px;
  font-size: 92%;
}

th {
  background-color: #001538;
  color: white;
  font-weight: normal;
  font-family: "Overpass", sans-serif !important;
  padding: 4px;
  padding-top: 6px;
  text-align: left;
  border-left: 1px solid gray;
  font-weight: 200;
}

td {
  border-left: 1px dotted gray;
  border-bottom: 1px dotted gray;
}

.icon {
  text-align: center;
}

.icon span {
  cursor: pointer;
}

.clickedItem {
  background-color: #f0bda6 !important;
}

.td-small {
  width: 3ex;
}

button {
  font-family: overpass, arial;
  background-color: #da6f38 !important;
  border: none !important;
  font-size: 100%;
  font-weight: 400;
  border: none;
  padding: 7px 13px 5px 13px;
  margin: 8px 5px;
  color: white;
  width: auto;
  cursor: pointer;
}

.clickableRma {
  cursor: pointer;
}
</style>
